import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Bell,
  Send,
  CheckCircle2,
  AlertCircle,
  Clock,
  Users,
  Loader2,
  History,
  Bot,
  MousePointerClick,
  Eye,
  Gauge,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";

/**
 * Page admin de gestion des Alertes Stock 3x8 (Novelis).
 *
 * 3 crons quotidiens : 05h30 / 13h30 / 21h30 Europe/Zurich (30 min avant chaque
 * changement d'équipe). Envoie un email récap des slots ≤ seuil (défaut 2) à
 * tous les field_operator + admin actifs.
 *
 * Endpoints : /api/admin/shift-alerts/{status,preview,send-now,logs}
 */
export default function ShiftAlertSettings() {
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, l] = await Promise.all([
        api.get("/admin/shift-alerts/status"),
        api.get("/admin/shift-alerts/preview"),
        api.get("/admin/shift-alerts/logs?limit=30"),
      ]);
      setStatus(s.data);
      setPreview(p.data);
      setLogs(l.data.logs || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sendNow = async () => {
    setSending(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/admin/shift-alerts/send-now?force=true");
      setLastResult(data);
      if (data.sent > 0) {
        toast.success(`Alerte envoyée à ${data.sent} destinataire${data.sent > 1 ? "s" : ""}`);
      } else if (data.failed > 0) {
        toast.error(`Échec : ${data.failed} envoi(s) raté(s)`);
      } else if (data.skipped_ras) {
        toast.info("Aucune alerte (RAS) — pas d'envoi");
      } else {
        toast.info(data.note || "Aucun destinataire");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-CH", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="py-16 text-center text-[#575A57]" data-testid="shift-alert-loading">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
        Chargement…
      </div>
    );
  }

  const sched = status?.scheduler || {};
  const recipients = status?.recipients || [];
  const alerts = preview?.alerts || [];
  const stats = preview?.stats || {};
  const threshold = preview?.threshold ?? 2;

  return (
    <div className="space-y-6" data-testid="shift-alert-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-[#C05C47]" />
          <h2 className="font-heading text-xl font-semibold text-[#1A1C1A]">
            Alertes stock 3x8 (Novelis)
          </h2>
        </div>
        <Link
          to="/parametres"
          className="text-sm text-[#575A57] hover:text-[#1F4E3D] flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" /> Retour aux paramètres
        </Link>
      </div>

      {/* État du scheduler */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Clock className="w-4 h-4 text-[#1F4E3D]" />
          Planificateur (3 crons quotidiens)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="shift-alert-scheduler-status">
          <StatusCell
            label="Activé"
            value={sched.running ? "Oui" : "Non"}
            color={sched.running ? "#1F4E3D" : "#A3A6A3"}
            icon={sched.running ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
          />
          <StatusCell
            label="Seuil bas"
            value={`≤ ${threshold} unité${threshold > 1 ? "s" : ""}`}
            color="#C1A063"
            icon={<Gauge className="w-3.5 h-3.5" />}
          />
          <StatusCell
            label="Fuseau"
            value={sched.timezone || "Europe/Zurich"}
            color="#575A57"
          />
          <StatusCell
            label="Périmètre"
            value="Tous AUTOMATIQUE"
            color="#575A57"
          />
        </div>

        {/* Liste des shifts */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="shift-alert-times">
          {(sched.shifts || []).map((s, i) => {
            const nextRun = (sched.next_runs || []).find((nr) => nr.id === `shift_alert_${i}`);
            return (
              <div key={i} className="bg-[#F4F4F0] border border-[#E5E5E0] rounded p-3" data-testid={`shift-${i}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Bell className="w-3.5 h-3.5 text-[#C05C47]" />
                  <span className="text-sm font-medium text-[#1A1C1A]">
                    {String(s.hour).padStart(2, "0")}h{String(s.minute).padStart(2, "0")}
                  </span>
                </div>
                <div className="text-xs text-[#575A57]">{s.label}</div>
                <div className="text-[10px] text-[#849F90] mt-1 font-mono">
                  Prochain : {nextRun ? fmtDate(nextRun.next_run_at) : "—"}
                </div>
              </div>
            );
          })}
        </div>

        {!sched.configured && (
          <p className="text-xs text-[#A04E2C] bg-[#FBEEEA] border border-[#E8B0A0] rounded p-2 mt-3" data-testid="shift-alert-not-configured">
            ⚠️ <strong>Resend non configuré</strong>. Définissez <code className="font-mono">RESEND_API_KEY</code> et <code className="font-mono">SENDER_EMAIL</code> puis redémarrez.
          </p>
        )}
        <p className="text-xs text-[#575A57] mt-3">
          💡 Configurer le seuil : variable <code className="font-mono">SHIFT_ALERT_THRESHOLD</code> (défaut 2). Surcharge destinataires : <code className="font-mono">SHIFT_ALERT_RECIPIENTS</code> (CSV).
        </p>
      </section>

      {/* Aperçu live des alertes */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5" data-testid="shift-alert-preview">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Eye className="w-4 h-4 text-[#1F4E3D]" />
          Aperçu live ({stats.total || 0} slot{(stats.total || 0) > 1 ? "s" : ""} à alerter)
        </h3>
        <div className="grid grid-cols-3 gap-3 mb-3">
          <StatusCell label="Critiques (vides)" value={stats.critical || 0} color="#C05C47" />
          <StatusCell label={`Bas (≤${threshold})`} value={stats.low || 0} color="#C1A063" />
          <StatusCell label="Distributeurs concernés" value={stats.distributors_impacted || 0} color="#1F4E3D" />
        </div>
        {alerts.length === 0 ? (
          <p className="text-sm text-[#849F90] bg-[#E8F2EC] border border-[#A8C9B6] rounded p-3 flex items-center gap-2" data-testid="shift-alert-ras">
            <CheckCircle2 className="w-4 h-4" /> Aucun slot sous le seuil — RAS pour le prochain shift.
          </p>
        ) : (
          <div className="border border-[#E5E5E0] rounded max-h-80 overflow-y-auto" data-testid="shift-alert-preview-list">
            {alerts.map((a, i) => (
              <div
                key={i}
                className="px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm flex items-center gap-3 flex-wrap hover:bg-[#F9F9F6]"
                data-testid={`preview-alert-${i}`}
              >
                <Badge
                  variant="outline"
                  className={`text-[10px] ${a.severity === "critical" ? "bg-[#FBEEEA] text-[#A04E2C] border-[#E8B0A0]" : "bg-[#FBF4E9] text-[#8C6B2F] border-[#E0D0AC]"}`}
                >
                  {a.severity === "critical" ? "VIDE" : `BAS ${a.total_qty}/${a.capacity || "—"}`}
                </Badge>
                <span className="text-xs font-mono text-[#575A57] w-24 flex-shrink-0 truncate">
                  {a.distributor_name}
                </span>
                <span className="text-[10px] text-[#849F90] w-20 flex-shrink-0">
                  {a.slot !== null && a.slot !== undefined ? `Slot ${a.slot}` : "Perpétuel"}
                </span>
                <span className="text-sm text-[#1A1C1A] flex-1 min-w-[180px]">
                  {a.product_name}
                  {a.is_perpetual && (
                    <Badge className="ml-2 bg-[#7A6FB0] text-white text-[9px]">BOISSON</Badge>
                  )}
                </span>
                <span className={`text-xs font-mono ${a.severity === "critical" ? "text-[#C05C47]" : "text-[#C1A063]"}`}>
                  {a.total_qty} unit.
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Destinataires */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Users className="w-4 h-4 text-[#1F4E3D]" />
          Destinataires actuels ({recipients.length})
        </h3>
        {recipients.length === 0 ? (
          <p className="text-sm text-[#575A57]" data-testid="shift-alert-no-recipients">
            Aucun destinataire. Créez au moins un utilisateur <code>field_operator</code> ou <code>admin</code>.
          </p>
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="shift-alert-recipients">
            {recipients.map((r, i) => (
              <li
                key={i}
                className="flex items-center gap-2 bg-[#F4F4F0] border border-[#E5E5E0] rounded px-3 py-1.5 text-sm"
              >
                <Bell className="w-3.5 h-3.5 text-[#C05C47] flex-shrink-0" />
                <span className="font-medium text-[#1A1C1A]">{r.name}</span>
                <span className="text-[#575A57] truncate font-mono text-xs">— {r.email}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Action manuelle */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <Send className="w-4 h-4 text-[#1F4E3D]" />
          Tester l&apos;envoi maintenant
        </h3>
        <p className="text-sm text-[#575A57] mb-3">
          Force l&apos;envoi immédiat avec l&apos;aperçu ci-dessus. Permet de valider le rendu avant qu&apos;un shift se déclenche pour de vrai.
        </p>
        <Button
          onClick={sendNow}
          disabled={sending || recipients.length === 0}
          data-testid="shift-alert-send-now-btn"
          className="bg-[#C05C47] hover:bg-[#A04E2C] text-white"
        >
          {sending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
          Envoyer maintenant
        </Button>

        {lastResult && (
          <div
            className={`mt-4 rounded p-3 text-sm border ${
              lastResult.sent > 0 ? "bg-[#E8F2EC] border-[#A8C9B6] text-[#1F4E3D]" : "bg-[#FBEEEA] border-[#E8B0A0] text-[#A04E2C]"
            }`}
            data-testid="shift-alert-last-result"
          >
            <strong>{lastResult.sent} envoyé(s)</strong> ·{" "}
            <strong>{lastResult.failed} échec(s)</strong> ·{" "}
            <strong>{lastResult.total_alerts} alerte(s) dans l&apos;email</strong>
            {lastResult.recipients && lastResult.recipients.some((r) => !r.success) && (
              <ul className="mt-2 ml-4 list-disc text-xs">
                {lastResult.recipients
                  .filter((r) => !r.success)
                  .map((r, i) => (
                    <li key={i} className="font-mono">
                      {r.to} : {r.error}
                    </li>
                  ))}
              </ul>
            )}
          </div>
        )}
      </section>

      {/* Historique */}
      <section className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <h3 className="font-heading text-base font-medium text-[#1A1C1A] mb-3 flex items-center gap-2">
          <History className="w-4 h-4 text-[#1F4E3D]" />
          Historique des envois ({logs.length})
        </h3>
        {logs.length === 0 ? (
          <p className="text-sm text-[#575A57]">Aucun envoi enregistré pour l&apos;instant.</p>
        ) : (
          <div className="border border-[#E5E5E0] rounded max-h-96 overflow-y-auto" data-testid="shift-alert-logs">
            {logs.map((log) => (
              <div
                key={log.id}
                className="px-3 py-2 border-b border-[#E5E5E0] last:border-b-0 text-sm flex items-center gap-3 flex-wrap hover:bg-[#F9F9F6]"
                data-testid={`shift-alert-log-${log.id}`}
              >
                {log.sent > 0 ? (
                  <CheckCircle2 className="w-4 h-4 text-[#1F4E3D] flex-shrink-0" />
                ) : log.skipped_ras ? (
                  <CheckCircle2 className="w-4 h-4 text-[#849F90] flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-4 h-4 text-[#C05C47] flex-shrink-0" />
                )}
                <span className="text-xs font-mono text-[#575A57] w-28 flex-shrink-0">
                  {fmtDate(log.sent_at)}
                </span>
                <Badge
                  variant="outline"
                  className={`text-[10px] ${log.source === "scheduler" ? "bg-[#F1EAF8] text-[#5B3A8C] border-[#C9B5E0]" : "bg-[#E8EEF6] text-[#1F3A66] border-[#A8BBD6]"}`}
                >
                  {log.source === "scheduler" ? (
                    <><Bot className="w-3 h-3 mr-1" /> Cron</>
                  ) : (
                    <><MousePointerClick className="w-3 h-3 mr-1" /> Manuel</>
                  )}
                </Badge>
                <span className="text-[10px] text-[#849F90] w-32 truncate">{log.shift_label || "—"}</span>
                <span className="text-xs text-[#1A1C1A] flex-1 min-w-[160px]">
                  {log.skipped_ras ? (
                    <span className="text-[#849F90] italic">RAS (skip)</span>
                  ) : (
                    <>
                      <strong className="text-[#1F4E3D]">{log.sent}</strong> envoyé(s) ·
                      {log.failed > 0 && <strong className="text-[#C05C47] ml-1">{log.failed} échec(s) ·</strong>}
                      <span className="ml-1">{log.total_alerts} alerte(s)</span>
                    </>
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function StatusCell({ label, value, color, icon }) {
  return (
    <div className="bg-[#F4F4F0] border border-[#E5E5E0] rounded p-2">
      <div className="text-[10px] uppercase tracking-wider text-[#575A57]">{label}</div>
      <div className="text-sm font-mono mt-0.5 flex items-center gap-1.5" style={{ color }}>
        {icon}
        {value}
      </div>
    </div>
  );
}
